import { useEffect, useRef, useState } from 'react';
import * as signalR from '@microsoft/signalr';

const useSignalR = () => {
    let params = {};
    const connectionRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);
    const [connectionSignalR, setConnectionSignalR] = useState(null);

    useEffect(() => {
        // connect()
        return () => {
            // Cleanup on unmount
            if (connectionRef.current) {
                connectionRef.current.stop();
            }
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
            }
        };
    }, []);

    const initSignalR = async (hubUrl, token, srvEventHandlers = {}, eventHandlers = {}, reconnectInterval = 5000, configureLogging = signalR.LogLevel.None) => {
        params = { hubUrl, token, srvEventHandlers, eventHandlers, reconnectInterval, configureLogging };
    }

    const connectSignalR = async () => {
        if (connectionRef.current && connectionRef.current.state === signalR.HubConnectionState.Connected) {
            if (params?.configureLogging != signalR.LogLevel.None)
                console.info("Already connected to SignalR.");
            return;
        }

        const {
            hubUrl,
            token,
            srvEventHandlers,
            eventHandlers,
            reconnectInterval,
            configureLogging
        } = params;

        {
            // Initialize the connection
            const connection = new signalR.HubConnectionBuilder()
                .withUrl(hubUrl, {
                    accessTokenFactory: () => token,
                })
                .withAutomaticReconnect()
                .configureLogging(configureLogging)
                .build();

            connectionRef.current = connection;
            setConnectionSignalR(connectionRef.current)

            connection.onreconnecting(async () => {
                if (eventHandlers.onreconnecting)
                    await eventHandlers.onreconnecting()

                if (eventHandlers.onStatusChange)
                    await eventHandlers.onStatusChange(signalR.HubConnectionState.Reconnecting)
            });
            connection.onreconnected(async () => {
                if (eventHandlers.onreconnected)
                    await eventHandlers.onreconnected()

                if (eventHandlers.onStatusChange)
                    await eventHandlers.onStatusChange(signalR.HubConnectionState.Connected)
            });
            connection.onclose(async () => {

                if (eventHandlers.onclose)
                    await eventHandlers.onclose()

                handleReconnect(reconnectInterval, eventHandlers);
            });

            try {
                await connection.start();

                if (eventHandlers.start)
                    eventHandlers.start()

                if (eventHandlers.onStatusChange)
                    eventHandlers.onStatusChange(signalR.HubConnectionState.Connected)

                // Setup event listeners from eventHandlers
                Object.keys(srvEventHandlers).forEach((event) =>
                    connection.on(event, srvEventHandlers[event])
                );

            } catch (error) {
                console.error('Connection failed:', error);
                handleReconnect(reconnectInterval, eventHandlers);
            }
        }
    };

    const handleReconnect = (reconnectInterval, eventHandlers) => {

        if (reconnectTimeoutRef.current)
            return; // Avoid multiple reconnect attempts

        if (eventHandlers.onStatusChange)
            eventHandlers.onStatusChange(signalR.HubConnectionState.Disconnected)

        reconnectTimeoutRef.current = setTimeout(() => {
            reconnectTimeoutRef.current = null;
            connectSignalR();
        }, reconnectInterval);
    };

    const sendToSignalR = async (methodName, args) => {
        try {
            // if ((!connectionRef.current ||
            //     connectionRef.current.state !== signalR.HubConnectionState.Connected) &&
            //     params?.configureLogging != signalR.LogLevel.None) {
            //     console.info("SignalR Hub is not connected!");
            //     return;
            // }

            await connectionRef?.current?.invoke(methodName, args);
        } catch (err) {
            console.error(`Error sending ${methodName}, error message: `, err);
        }
    };

    const disconnectSignalR = async () => {
        try {
            // Clear any pending reconnection attempts
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }

            // Stop the connection if it exists
            if (connectionRef.current) {
                await connectionRef.current.stop();
                if (params?.configureLogging != signalR.LogLevel.None) {
                    console.info("Disconnected from SignalR.");
                }
            }

            // Clear the connection reference
            connectionRef.current = null;
            setConnectionSignalR(null);
        } catch (error) {
            console.error("Error disconnecting from SignalR:", error);
        }
    };

    return { connection: connectionRef.current, initSignalR, connectSignalR, sendToSignalR, disconnectSignalR };
};

export default useSignalR;
